import { JobsClientError } from '@okrapdf/protocol/http-jobs-client';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { Data, Effect } from 'effect';
import { jobsClient } from '../jobsClient';
import { okraApi } from '../okraApi';
import type {
  JobListResponse,
  JobStatus,
  JobListItem,
  JobChildListResponse,
  JobPageRow,
  JobPageStatus,
  JobChunkRow,
} from '../types';

export class JobsEndpointError extends Data.TaggedError('JobsEndpointError')<{
  readonly operation: 'query';
}> {}

const jobsEndpointCauses = new WeakMap<JobsEndpointError, unknown>();

function jobsEndpointFailure(cause: unknown): JobsEndpointError {
  const error = new JobsEndpointError({ operation: 'query' });
  jobsEndpointCauses.set(error, cause);
  return error;
}

export function jobsEndpointErrorCause(error: JobsEndpointError): unknown {
  return jobsEndpointCauses.get(error);
}

export type ListJobsParams = {
  status?: JobStatus | 'all';
  limit?: number;
  /** Opaque `next_cursor` from the previous page; omit or `null` for the first page. */
  cursor?: string | null;
};

/** Page size the Jobs feed loads per request; `Load more` walks `next_cursor`. */
export const JOBS_FEED_PAGE_SIZE = 50;

export const listJobsEffect = Effect.fn('web.api.jobs.list')(function* (
  params: ListJobsParams | void,
): Effect.fn.Return<JobListResponse, JobsEndpointError> {
  return yield* Effect.tryPromise({
    try: () =>
      jobsClient.listJobs({
        status: params?.status,
        limit: params?.limit ?? 100,
        ...(params?.cursor ? { cursor: params.cursor } : {}),
      }),
    catch: jobsEndpointFailure,
  });
});

/**
 * Concatenate the pages of the jobs feed, newest activity first. The API
 * orders by `updated_at`, so a job that progressed between two page reads can
 * appear at the top of a refetched first page AND on the later page that was
 * read before it moved — keep the first (newest) copy.
 */
export function flattenJobPages(pages: readonly JobListResponse[]): JobListItem[] {
  const seen = new Set<string>();
  const jobs: JobListItem[] = [];
  for (const page of pages) {
    for (const job of page.data) {
      const key = job.job_id || job.id;
      if (seen.has(key)) continue;
      seen.add(key);
      jobs.push(job);
    }
  }
  return jobs;
}

/**
 * [A.6.5a] Jobs endpoints delegate to the shared contract client
 * (`@okrapdf/protocol/http-jobs-client`) — URLs, query encoding, and response
 * shapes are owned by the contract package; this module only maps results into
 * RTK Query's `{ data } | { error }` union and keeps the cache tags stable.
 */
export function toQueryErrorEffect(error: unknown): Effect.Effect<{ error: FetchBaseQueryError }> {
  return Effect.sync(() => {
    if (error instanceof JobsClientError) {
      const data = { message: error.message, code: error.code, next_actions: error.nextActions };
      if (typeof error.status === 'number') return { error: { status: error.status, data } };
      return { error: { status: 'CUSTOM_ERROR', error: error.message, data } };
    }
    const message = error instanceof Error ? error.message : 'Jobs request failed';
    return { error: { status: 'CUSTOM_ERROR', error: message, data: { message } } };
  });
}

async function runJobsQuery<A>(
  evaluate: () => Promise<A>,
): Promise<{ data: A } | { error: FetchBaseQueryError }> {
  return Effect.runPromise(
    Effect.tryPromise({
      try: async () => ({ data: await evaluate() }),
      catch: jobsEndpointFailure,
    }).pipe(Effect.catch((error) => toQueryErrorEffect(jobsEndpointErrorCause(error) ?? error))),
  );
}

export const jobsEndpoints = okraApi.injectEndpoints({
  endpoints: (build) => ({
    listJobs: build.query<JobListResponse, ListJobsParams | void>({
      async queryFn(params) {
        return runJobsQuery(() => Effect.runPromise(listJobsEffect(params)));
      },
      providesTags: ['Jobs'],
    }),
    /**
     * Cursor-paginated jobs feed: the first page renders as soon as it lands
     * and `fetchNextPage` follows `next_cursor`. Polling refetches the cached
     * pages in order, which keeps in-flight rows live without one 100-row
     * hydrated request per tick.
     */
    jobsFeed: build.infiniteQuery<
      JobListResponse,
      { status?: JobStatus | 'all'; limit?: number },
      string | null
    >({
      infiniteQueryOptions: {
        initialPageParam: null,
        getNextPageParam: (lastPage) =>
          lastPage.has_more && lastPage.next_cursor ? lastPage.next_cursor : undefined,
      },
      async queryFn({ queryArg, pageParam }) {
        return runJobsQuery(() =>
          Effect.runPromise(
            listJobsEffect({
              status: queryArg.status,
              limit: queryArg.limit ?? JOBS_FEED_PAGE_SIZE,
              cursor: pageParam,
            }),
          ),
        );
      },
      providesTags: ['Jobs'],
    }),
    getJob: build.query<JobListItem, string>({
      async queryFn(jobId) {
        return runJobsQuery(() => jobsClient.getJob(jobId));
      },
      providesTags: (_r, _e, id) => [{ type: 'Jobs', id } as const],
    }),
    listJobPages: build.query<
      JobChildListResponse<JobPageRow>,
      { jobId: string; status?: JobPageStatus | 'failed'; limit?: number }
    >({
      async queryFn({ jobId, status, limit }) {
        return runJobsQuery(() => jobsClient.listJobPages({ jobId, status, limit }));
      },
      providesTags: (_r, _e, { jobId }) => [{ type: 'Jobs', id: jobId } as const],
    }),
    listJobChunks: build.query<
      JobChildListResponse<JobChunkRow>,
      { jobId: string; status?: JobChunkRow['status'] | 'error'; limit?: number }
    >({
      async queryFn({ jobId, status, limit }) {
        return runJobsQuery(() => jobsClient.listJobChunks({ jobId, status, limit }));
      },
      providesTags: (_r, _e, { jobId }) => [{ type: 'Jobs', id: jobId } as const],
    }),
    cancelJob: build.mutation<JobListItem, string>({
      async queryFn(jobId) {
        return runJobsQuery(() => jobsClient.cancelJob(jobId));
      },
      invalidatesTags: (_r, _e, jobId) => ['Jobs', { type: 'Jobs', id: jobId } as const],
    }),
    retryJob: build.mutation<JobListItem, string>({
      async queryFn(jobId) {
        return runJobsQuery(() => jobsClient.retryJob(jobId));
      },
      invalidatesTags: (_r, _e, jobId) => ['Jobs', { type: 'Jobs', id: jobId } as const],
    }),
    resumeJob: build.mutation<JobListItem, string>({
      async queryFn(jobId) {
        return runJobsQuery(() => jobsClient.resumeJob(jobId));
      },
      invalidatesTags: (_r, _e, jobId) => ['Jobs', { type: 'Jobs', id: jobId } as const],
    }),
  }),
});

export const {
  useListJobsQuery,
  useJobsFeedInfiniteQuery,
  useGetJobQuery,
  useListJobPagesQuery,
  useListJobChunksQuery,
  useCancelJobMutation,
  useRetryJobMutation,
  useResumeJobMutation,
} = jobsEndpoints;
